import { useState, useEffect } from 'react';

export default function Footer() {
  const currentYear = new Date().getFullYear();
  const [showBackToTop, setShowBackToTop] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setShowBackToTop(window.scrollY > 500);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <footer className="bg-gray-900 text-white py-16 relative">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
          <div>
            <img
              src="https://static.readdy.ai/image/2fb69e05e600ee45b7cfaa2e2ddcddf8/b782f25abe3d9999e649c275d9ff9c00.png"
              alt="Logo"
              className="h-16 w-auto mb-6 brightness-0 invert"
            />
            <p className="text-sm text-gray-400 leading-relaxed">
              Controle de ponto pensado para a realidade do agronegócio brasileiro.
            </p>
          </div>

          <div>
            <h3 className="text-base font-bold mb-4">Navegação</h3>
            <ul className="space-y-3">
              <li>
                <button
                  onClick={() => scrollToSection('home')}
                  className="text-sm text-gray-400 hover:text-teal-400 transition-colors"
                >
                  Início
                </button>
              </li>
              <li>
                <button
                  onClick={() => scrollToSection('problema')}
                  className="text-sm text-gray-400 hover:text-teal-400 transition-colors"
                >
                  Problema
                </button>
              </li>
              <li>
                <button
                  onClick={() => scrollToSection('solucao')}
                  className="text-sm text-gray-400 hover:text-teal-400 transition-colors"
                >
                  Solução
                </button>
              </li>
              <li>
                <button
                  onClick={() => scrollToSection('beneficios')}
                  className="text-sm text-gray-400 hover:text-teal-400 transition-colors"
                >
                  Benefícios
                </button>
              </li>
              <li>
                <button
                  onClick={() => scrollToSection('faq')}
                  className="text-sm text-gray-400 hover:text-teal-400 transition-colors"
                >
                  FAQ
                </button>
              </li>
            </ul>
          </div>

          <div>
            <h3 className="text-base font-bold mb-4">Empresa</h3>
            <ul className="space-y-3">
              <li>
                <button
                  onClick={() => scrollToSection('sobre')}
                  className="text-sm text-gray-400 hover:text-teal-400 transition-colors"
                >
                  Sobre Nós
                </button>
              </li>
              <li>
                <button
                  onClick={() => scrollToSection('contato')}
                  className="text-sm text-gray-400 hover:text-teal-400 transition-colors"
                >
                  Fale Conosco
                </button>
              </li>
            </ul>
          </div>

          <div>
            <h3 className="text-base font-bold mb-4">Contato</h3>
            <ul className="space-y-3">
              <li className="flex items-start space-x-3">
                <i className="ri-phone-line text-teal-400 text-lg mt-0.5"></i>
                <span className="text-sm text-gray-400">(16) 98109-1060</span>
              </li>
              <li className="flex items-start space-x-3">
                <i className="ri-mail-line text-teal-400 text-lg mt-0.5"></i>
                <span className="text-sm text-gray-400">comercial@agroponto.com.br</span>
              </li>
              <li className="flex items-start space-x-3">
                <i className="ri-map-pin-line text-teal-400 text-lg mt-0.5"></i>
                <span className="text-sm text-gray-400">Av. Alonso Y Alonso, 1219 SALA 602 - Franca-SP</span>
              </li>
            </ul>
          </div>
        </div>

        <div className="border-t border-gray-800 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <p className="text-sm text-gray-400">
              {currentYear} Controle de Ponto Rural. Todos os direitos reservados.
            </p>
            <a
              href="https://readdy.ai/?ref=logo"
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-gray-400 hover:text-teal-400 transition-colors"
            >
              Powered by Readdy
            </a>
          </div>
        </div>
      </div>

      {/* Botão Voltar ao Topo */}
      <button
        onClick={scrollToTop}
        className={`fixed left-8 bottom-8 z-50 w-14 h-14 bg-gradient-to-r from-teal-600 to-cyan-600 text-white rounded-full shadow-2xl hover:shadow-[0_0_30px_rgba(20,184,166,0.6)] transition-all duration-300 flex items-center justify-center group cursor-pointer ${
          showBackToTop ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10 pointer-events-none'
        }`}
        aria-label="Voltar ao topo"
      >
        <i className="ri-arrow-up-line text-2xl group-hover:-translate-y-1 transition-transform"></i>
        
        {/* Efeito de brilho */}
        <div className="absolute inset-0 rounded-full bg-gradient-to-r from-cyan-400 to-teal-400 opacity-0 group-hover:opacity-30 blur transition-opacity duration-300"></div>
        
        {/* Anel animado */}
        <div className="absolute inset-0 rounded-full border-2 border-white/30 animate-ping"></div>
      </button>

      {/* Botão WhatsApp */}
      <a
        href="https://wa.me/5516981091060"
        target="_blank"
        rel="noopener noreferrer"
        className="fixed right-8 bottom-8 z-50 w-14 h-14 bg-[#25D366] text-white rounded-full shadow-2xl hover:shadow-[0_0_30px_rgba(37,211,102,0.6)] hover:scale-110 transition-all duration-300 flex items-center justify-center group cursor-pointer"
        aria-label="Fale conosco pelo WhatsApp"
      >
        <i className="ri-whatsapp-line text-3xl"></i>
        
        {/* Efeito de pulso */}
        <div className="absolute inset-0 rounded-full bg-[#25D366] animate-ping opacity-30"></div>
      </a>
    </footer>
  );
}
